import {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import {Modal} from '@ui/molecules/modal';
import {Body1, Body2} from '@ui/atoms/typography';
import {CancelIcon} from '@ui/atoms/icons';
import {NotificationList} from './notification-list';
import {useNotificationInbox} from './use-notification-inbox';

export interface NotificationDrawerProps {
  onClose: () => void;
  railOffset?: number;
}

export const NotificationDrawer = ({
  onClose,
  railOffset = 0,
}: NotificationDrawerProps) => {
  const inbox = useNotificationInbox();
  const navigate = useNavigate();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const visibleNotifications =
    filter === 'unread'
      ? inbox.notifications.filter(({unread}) => unread)
      : inbox.notifications;

  useEffect(() => {
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <Modal
      open
      fullHeight
      onClose={onClose}
      zIndex={20}
      leftOffset={railOffset}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="notification-drawer-title"
        style={{
          left: 0,
          maxWidth: `min(320px, calc(100vw - ${railOffset}px))`,
        }}
        className="absolute top-0 flex h-full w-full flex-col border-r border-borderLight bg-white shadow-lg">
        <header className="flex items-center justify-between border-b border-borderLight px-4 py-4 sm:px-6">
          <div className="flex items-center gap-2">
            <Body1 id="notification-drawer-title" kind="textDark" weight="bold">
              Notifications
            </Body1>
            {inbox.unreadCount > 0 && (
              <span className="rounded-full bg-red400 px-2 py-0.5 text-xs font-semibold text-alwaysWhite">
                {inbox.unreadCount > 99 ? '99+' : inbox.unreadCount}
              </span>
            )}
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label="Close notifications"
            onClick={onClose}
            className="rounded-md p-2 hover:bg-backgroundDark focus:outline-none focus-visible:ring-2 focus-visible:ring-purple300">
            <CancelIcon style={{strokeWidth: 2}} />
          </button>
        </header>

        <div className="flex items-center justify-between border-b border-borderLight px-4 py-3 sm:px-6">
          <div className="flex gap-2" role="tablist" aria-label="Notification filter">
            {(['all', 'unread'] as const).map((option) => (
              <button
                key={option}
                type="button"
                role="tab"
                aria-selected={filter === option}
                onClick={() => setFilter(option)}
                className={`rounded-lg px-3 py-2 text-sm font-medium ${
                  filter === option
                    ? 'bg-purple300 text-alwaysWhite'
                    : 'text-textBody hover:bg-backgroundDark'
                }`}>
                {option === 'all' ? 'All' : `Unread (${inbox.unreadCount})`}
              </button>
            ))}
          </div>
          <button
            type="button"
            disabled={!inbox.unreadCount}
            onClick={inbox.markAllRead}
            className="text-sm font-medium text-purple300 disabled:cursor-not-allowed disabled:text-textMuted">
            Mark all read
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <NotificationList
            notifications={visibleNotifications}
            isLoading={inbox.isLoading}
            isError={inbox.isError}
            onRetry={() => inbox.refetch()}
            onOpen={onClose}
            onMarkRead={inbox.markRead}
            onMarkUnread={inbox.markUnread}
            onDismiss={inbox.dismiss}
          />
        </div>

        <footer className="border-t border-borderLight bg-white p-4 text-center">
          <button
            type="button"
            onClick={() => {
              navigate('/alerts');
              onClose();
            }}>
            <Body2 kind="purple300" weight="semibold">
              View all notifications
            </Body2>
          </button>
        </footer>
      </aside>
    </Modal>
  );
};
